import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/switchMap';
import { WebServiceService } from './web-service.service';
import { MultiCompanyViewContext } from './multi-company-view-context';

export interface CompanyViewTokens {
  company_id: string;
  access_token: string;
  user_access_token: string;
}

@Injectable()
export class MultiCompanyViewTokenService {
  private tokenCache: CompanyViewTokens[] = [];
  private inFlightRequests: { [companyId: string]: Observable<CompanyViewTokens> } = {};

  constructor(private webService: WebServiceService) {}

  setActiveViewCompanyId(companyId: string | null): void {
    MultiCompanyViewContext.setActiveViewCompanyId(companyId);
  }

  clearActiveViewCompanyId(): void {
    MultiCompanyViewContext.clearActiveViewCompanyId();
  }

  clearCache(): void {
    this.tokenCache = [];
    this.inFlightRequests = {};
    this.clearActiveViewCompanyId();
  }

  getCachedTokens(companyId: string): CompanyViewTokens | null {
    const key = companyId != null ? companyId.toString() : '';
    if (!key) {
      return null;
    }
    return this.tokenCache.find(entry => entry.company_id === key) || null;
  }

  getOrFetchTokens(companyId: string): Observable<CompanyViewTokens> {
    const key = companyId != null ? companyId.toString() : '';
    if (!key) {
      return Observable.throw('Invalid company id');
    }

    const cached = this.getCachedTokens(key);
    if (cached) {
      return Observable.of(cached);
    }

    if (this.inFlightRequests[key]) {
      return this.inFlightRequests[key];
    }

    const request$ = this.fetchTokensFromApi(key)
      .do(tokens => {
        if (!this.getCachedTokens(key)) {
          this.tokenCache.push(tokens);
        }
        delete this.inFlightRequests[key];
      })
      .catch(error => {
        delete this.inFlightRequests[key];
        return Observable.throw(error);
      })
      .share();

    this.inFlightRequests[key] = request$;
    return request$;
  }

  private fetchTokensFromApi(companyId: string): Observable<CompanyViewTokens> {
    const requestObj = { company_id: companyId };
    return this.webService.commonPostMethod('Companies/company_get_token', requestObj).switchMap(data => {
      if (!data.status) {
        return Observable.throw(data.message || 'Unable to get company token');
      }
      const accessToken = data.response;
      return this.webService
        .commonPostMethod('User_session_management/generate_token', requestObj)
        .switchMap(genData => {
          if (genData.status || genData.already_active) {
            return Observable.of({
              company_id: companyId,
              access_token: accessToken,
              user_access_token: genData.response ? genData.response : ''
            });
          }
          return Observable.throw(genData.message || 'Unable to get user token');
        });
    });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src4256039471/src/src/app/shared/services/multi-company-view-token.service.ts